import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BeakerIcon, 
  PlusIcon, 
  ChartBarIcon,
  PlayIcon,
  StopIcon,
  TrophyIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface ABTest {
  id: string;
  name: string;
  description: string;
  testType: 'SIMPLE' | 'MULTIVARIATE';
  status: 'SCHEDULED' | 'ACTIVE' | 'COMPLETED';
  targetMetric: string;
  startDate: string;
  endDate: string;
  winningVariantId?: string;
  variants: Array<{
    id: string;
    name: string;
    trafficAllocation: number;
  }>;
}

interface TestStats {
  active: number;
  completed: number;
  scheduled: number;
  totalConversions: number;
}

const ABTestDashboard: React.FC = () => {
  const [tests, setTests] = useState<ABTest[]>([]);
  const [stats, setStats] = useState<TestStats>({
    active: 0,
    completed: 0,
    scheduled: 0,
    totalConversions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatorProfileId, setCreatorProfileId] = useState<string | null>(null);

  useEffect(() => {
    fetchCreatorProfile();
  }, []);

  useEffect(() => {
    if (creatorProfileId) {
      fetchTests();
    }
  }, [creatorProfileId]);

  const fetchCreatorProfile = async () => {
    try {
      const response = await axios.get('/api/users/me');
      const userId = response.data.id;
      const profileResponse = await axios.get(`/api/creators/${userId}`);
      setCreatorProfileId(profileResponse.data.id);
    } catch (err) {
      console.error('Error fetching creator profile:', err);
      setError('Failed to load creator profile');
    }
  };

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/ab-testing/creator/${creatorProfileId}`);
      const allTests = response.data;
      setTests(allTests);

      // Calculate stats
      const newStats = {
        active: allTests.filter((t: ABTest) => t.status === 'ACTIVE').length,
        completed: allTests.filter((t: ABTest) => t.status === 'COMPLETED').length,
        scheduled: allTests.filter((t: ABTest) => t.status === 'SCHEDULED').length,
        totalConversions: 0, // Will be calculated from analytics if needed
      };
      setStats(newStats);
    } catch (err) {
      console.error('Error fetching tests:', err);
      setError('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const stopTest = async (testId: string) => {
    if (!window.confirm('Are you sure you want to stop this test?')) {
      return;
    }

    try {
      await axios.post(`/api/ab-testing/${testId}/stop`);
      fetchTests(); // Refresh list
    } catch (err) {
      console.error('Error stopping test:', err);
      alert('Failed to stop test');
    }
  };

  const deleteTest = async (testId: string) => {
    if (!window.confirm('Are you sure you want to delete this test? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/ab-testing/${testId}`);
      fetchTests(); // Refresh list
    } catch (err: any) {
      console.error('Error deleting test:', err);
      alert(err.response?.data?.error || 'Failed to delete test');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      ACTIVE: 'bg-green-100 text-green-800',
      COMPLETED: 'bg-gray-100 text-gray-800',
      SCHEDULED: 'bg-blue-100 text-blue-800',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BeakerIcon className="h-8 w-8 text-blue-600" />
              A/B Testing Dashboard
            </h1>
            <p className="mt-2 text-gray-600">
              Create experiments to optimize your content and offerings
            </p>
          </div>
          <Link
            to="/dashboard/ab-testing/create"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Create Test
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Tests</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <PlayIcon className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
            </div>
            <TrophyIcon className="h-10 w-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Scheduled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.scheduled}</p>
            </div>
            <ChartBarIcon className="h-10 w-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Tests</p>
              <p className="text-2xl font-bold text-gray-900">{tests.length}</p>
            </div>
            <ArrowTrendingUpIcon className="h-10 w-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Tests List */}
      {tests.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BeakerIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tests yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first A/B test to start optimizing your content and offerings
          </p>
          <Link
            to="/dashboard/ab-testing/create"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Create Your First Test
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variants
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target Metric
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tests.map((test) => (
                  <tr key={test.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{test.name}</div>
                        <div className="text-sm text-gray-500">{test.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{test.testType}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(test.status)}`}>
                        {test.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {test.variants.length} variants
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {test.targetMetric}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(test.startDate).toLocaleDateString()} -{' '}
                      {new Date(test.endDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/dashboard/ab-testing/${test.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View
                        </Link>
                        {test.status === 'ACTIVE' && (
                          <button
                            onClick={() => stopTest(test.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Stop
                          </button>
                        )}
                        {test.status !== 'ACTIVE' && (
                          <button
                            onClick={() => deleteTest(test.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ABTestDashboard;
